import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '../../contexts/AuthContext'
import { validateEmail, validatePassword, passwordsMatch } from '../../utils/validation'
import { PasswordStrength } from '../../components/PasswordStrength'
import {
  AuthContainer,
  AuthCard,
  Form,
  ButtonContainer,
  Divider,
  SignupText,
  StyledLink,
  InputGroup,
  Label,
  StyledInput,
  ErrorText,
} from './styles'

export function Register() {
  const navigate = useNavigate()
  const { register, isAuthenticated } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const validateField = (name, value) => {
    let error = ''

    if (name === 'email') {
      if (!value) {
        error = 'Email é obrigatório'
      } else if (!validateEmail(value)) {
        error = 'Email inválido'
      }
    }

    if (name === 'password') {
      const validation = validatePassword(value)
      if (!value) {
        error = 'Senha é obrigatória'
      } else if (!validation.isValid) {
        error = 'A senha não atende aos requisitos'
      }

      if (confirmPassword && !passwordsMatch(value, confirmPassword)) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'As senhas não coincidem' }))
      } else if (confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }))
      }
    }

    if (name === 'confirmPassword') {
      if (!value) {
        error = 'Confirmação de senha é obrigatória'
      } else if (!passwordsMatch(password, value)) {
        error = 'As senhas não coincidem'
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }))
    return !error
  }

  const onBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    if (name === 'email') validateField(name, email)
    if (name === 'password') validateField(name, password)
    if (name === 'confirmPassword') validateField(name, confirmPassword)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setTouched({ email: true, password: true, confirmPassword: true })
    const emailValid = validateField('email', email)
    const passwordValid = validateField('password', password)
    const confirmPasswordValid = validateField('confirmPassword', confirmPassword)

    if (!emailValid || !passwordValid || !confirmPasswordValid) {
      return
    }

    setIsLoading(true)

    try {
      await register(email, password)
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration')
      }
      toast.success('Conta criada! Verifique seu email para ativar sua conta.')
      navigate('/resend-verification', { state: { email, justRegistered: true } })
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Email já cadastrado')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Dados inválidos')
      } else {
        toast.error('Erro ao criar conta. Tente novamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid =
    !errors.email &&
    !errors.password &&
    !errors.confirmPassword &&
    email &&
    password &&
    confirmPassword &&
    validatePassword(password).isValid &&
    passwordsMatch(password, confirmPassword)

  return (
    <AuthContainer>
      <AuthCard>
        <h1>Criar Conta</h1>
        <p>Cadastre-se gratuitamente para acesso ilimitado</p>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <StyledInput
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (touched.email) validateField('email', e.target.value)
              }}
              onBlur={() => onBlur('email')}
              placeholder="seu@email.com"
              autoComplete="email"
              $hasError={touched.email && errors.email}
              $isValid={touched.email && !errors.email && email}
            />
            {touched.email && errors.email && <ErrorText>✗ {errors.email}</ErrorText>}
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Senha</Label>
            <StyledInput
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (touched.password) validateField('password', e.target.value)
              }}
              onBlur={() => onBlur('password')}
              placeholder="Crie uma senha forte"
              autoComplete="new-password"
              $hasError={touched.password && errors.password}
              $isValid={touched.password && !errors.password && password}
            />
            {touched.password && errors.password && <ErrorText>✗ {errors.password}</ErrorText>}
            {password && <PasswordStrength password={password} />}
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirmPassword">Confirmar Senha</Label>
            <StyledInput
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                if (touched.confirmPassword) validateField('confirmPassword', e.target.value)
              }}
              onBlur={() => onBlur('confirmPassword')}
              placeholder="Digite a senha novamente"
              autoComplete="new-password"
              $hasError={touched.confirmPassword && errors.confirmPassword}
              $isValid={touched.confirmPassword && !errors.confirmPassword && confirmPassword}
            />
            {touched.confirmPassword && errors.confirmPassword && <ErrorText>✗ {errors.confirmPassword}</ErrorText>}
          </InputGroup>

          <ButtonContainer>
            <button type="submit" disabled={!isFormValid || isLoading}>
              {isLoading ? 'Criando conta...' : 'Criar Conta'}
            </button>
          </ButtonContainer>
        </Form>

        <Divider />

        <SignupText>
          Já tem uma conta? <StyledLink to="/login">Fazer login</StyledLink>
        </SignupText>
      </AuthCard>
    </AuthContainer>
  )
}
